// src/screens/MarketplaceListScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { IoArrowBack, IoStar, IoStarHalf, IoStarOutline, IoDiamond, IoRibbon } from 'react-icons/io5';
import * as Ionicons from 'react-icons/io5';
import './MarketplaceListScreen.css';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoSidebar from '../components/Shared/KondoSidebar';

// Normalize helper to match category names regardless of accents
const normalizeStr = (str) => {
  return str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() : "";
};

// Componente Estrellas
const StarRating = ({ rating }) => {
  const totalStars = 5;
  let stars = [];
  for (let i = 1; i <= totalStars; i++) {
    if (i <= rating) {
      stars.push(<IoStar key={i} />);
    } else if (i - 0.5 <= rating) {
      stars.push(<IoStarHalf key={i} />);
    } else {
      stars.push(<IoStarOutline key={i} />);
    }
  }
  return <div className="star-box">{stars}</div>;
};

// Componente Precio
const PriceRange = ({ range }) => {
  const rangeStr = range && typeof range === 'string' ? range : "$";
  return (
    <div className="price-indicator">
      <span className={rangeStr.length >= 1 ? 'active' : ''}>$</span>
      <span className={rangeStr.length >= 2 ? 'active' : ''}>$</span>
      <span className={rangeStr.length >= 3 ? 'active' : ''}>$</span>
    </div>
  );
};

function MarketplaceListScreen() {
  const navigate = useNavigate();
  const { categoriaId } = useParams();

  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentCategory, setCurrentCategory] = useState(null);

  useEffect(() => {
    if (!categoriaId) return;

    const q = query(collection(db, 'marketplace_categorias'), where('activo', '==', true));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let found = false;
      snapshot.forEach(docSnap => {
        const cat = docSnap.data();
        if (normalizeStr(cat.nombre) === normalizeStr(categoriaId)) {
          setCurrentCategory({ idDoc: docSnap.id, ...cat });
          found = true;
        }
      });
      if (!found) {
        // Fallback standard styling based on Plomeria or defaults
        const isPlomeria = normalizeStr(categoriaId) === 'plomeria';
        setCurrentCategory({
          nombre: isPlomeria ? 'Plomería' : (categoriaId === 'servicio doméstico' ? 'Limpieza y Ayuda' : categoriaId.charAt(0).toUpperCase() + categoriaId.slice(1)),
          color: isPlomeria ? '#2563eb' : '#6348D1', // Beautiful blue for Plomería, purple standard fallback
          iconoNombre: isPlomeria ? 'IoWaterOutline' : 'IoStorefrontOutline',
          descripcion: isPlomeria ? 'Servicios de plomería profesional para tu hogar.' : `Encuentra a los mejores profesionales de ${categoriaId}`
        });
      }
    }, (error) => {
      console.error("Error loading category metadata:", error);
    });

    return () => unsubscribe();
  }, [categoriaId]);

  useEffect(() => {
    if (!categoriaId) return;

    const categoriaCapitalizada = categoriaId.charAt(0).toUpperCase() + categoriaId.slice(1);
    setLoading(true);

    // Lógica inteligente de categorías
    let categoriasBusqueda = [categoriaCapitalizada];
    if (normalizeStr(categoriaCapitalizada) === 'servicio domestico') {
      categoriasBusqueda = ['Servicio Doméstico', 'Limpieza'];
    } else if (normalizeStr(categoriaCapitalizada) === 'plomeria') {
      categoriasBusqueda = ['Plomería', 'Plomeria'];
    }

    const q = query(
      collection(db, "market_proveedores"),
      where("categoria", "in", categoriasBusqueda),
      where("estatus", "==", "activo"),
      orderBy("ratingPromedio", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const listaProveedores = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setProveedores(listaProveedores);
      setLoading(false);
    }, (error) => {
      console.error("Error al cargar proveedores: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [categoriaId]);

  const renderHeroIcon = (iconName) => {
    const IconComponent = Ionicons[iconName] || Ionicons['IoStorefrontOutline'];
    return <IconComponent className="marketplace-list-hero-icon-decor" />;
  };

  const renderSmallIcon = (iconName) => {
    const IconComponent = Ionicons[iconName] || Ionicons['IoStorefrontOutline'];
    return <IconComponent size={24} color="white" style={{ opacity: 0.95 }} />;
  };

  return (
    <div className="ks-page">
      {/* ── DESKTOP SIDEBAR ─────────────────────── */}
      <KondoSidebar activeRoute="/marketplace" />

      {/* ── MAIN AREA ────────────────────────────── */}
      <main className="ks-main">
        <div className="ks-scroll-area">
          <div className="ks-marketplace-header-sticky-wrapper">
            <KondoBrandHeader showBack={false} />
          </div>

          <div className="marketplace-content-wrapper">
            {/* Premium Scoped Hero Banner */}
            <div className="marketplace-header-stitch">
              <div
                className="marketplace-list-hero-card"
                style={
                  currentCategory?.color
                    ? { background: `linear-gradient(135deg, ${currentCategory.color}dd 0%, ${currentCategory.color} 100%)` }
                    : { background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)' }
                }
              >
                <div className="marketplace-list-hero-content">
                  <button onClick={() => navigate('/marketplace')} className="marketplace-list-glass-back-btn" title="Atrás">
                    <IoArrowBack size={20} color="white" />
                  </button>
                  <div className="marketplace-list-hero-text-col">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {currentCategory?.iconoNombre && renderSmallIcon(currentCategory.iconoNombre)}
                      <h1>
                        {currentCategory?.nombre || (normalizeStr(categoriaId) === 'plomeria' ? 'Plomería' : categoriaId)}
                      </h1>
                    </div>
                    <p>
                      {currentCategory?.descripcion || (normalizeStr(categoriaId) === 'plomeria' ? 'Servicios de plomería profesional para tu hogar.' : `Encuentra a los mejores profesionales de ${categoriaId}`)}
                    </p>
                  </div>
                </div>
                {currentCategory?.iconoNombre && renderHeroIcon(currentCategory.iconoNombre)}
              </div>
            </div>

            {loading && (
              <div className="loading-state">
                <h3>Buscando a los mejores profesionales...</h3>
              </div>
            )}

            {!loading && proveedores.length === 0 && (
              <div className="empty-state">
                <h3>No hay proveedores disponibles</h3>
                <p>Aún no tenemos profesionales activos en esta categoría.</p>
              </div>
            )}

            {!loading && (
              <div className="provider-list-container">
                {proveedores.map((prov, index) => {
                  const planId = prov.marketPlanId || 'tier_plata';
                  const isSocio = planId.toLowerCase().includes('socio');
                  const isDiamante = planId === 'tier_diamante';
                  const isOro = planId === 'tier_oro';
                  const isPremium = isDiamante || isOro || isSocio;

                  const badgeText = isSocio ? 'SOCIO EXCLUSIVO' : 'PATROCINADO';
                  const verifiedIconColor = isSocio ? '#fbbf24' : (isDiamante ? '#a855f7' : '#eab308');
                  const badgeBg = isSocio ? 'linear-gradient(135deg, #1f2937, #000000)' : (isDiamante ? 'linear-gradient(135deg, #a855f7, #9333ea)' : '#eab308');

                  return (
                    <div
                      key={prov.id}
                      className={`provider-card ${isPremium ? 'premium ' + (isSocio ? 'socio' : (isDiamante ? 'diamante' : 'oro')) : 'standard'}`}
                      onClick={() => navigate(`/marketplace/proveedor/${prov.id}`)}
                    >
                      {isPremium && (
                        <div className="premium-badge" style={{ background: badgeBg }}>
                          <span className="badge-text">{badgeText}</span>
                        </div>
                      )}

                      {isPremium ? (
                        <>
                          <div className="premium-top-section">
                            <div className="premium-info-col">
                              <div className="premium-line-1">
                                <img
                                  src={prov.logoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(prov.nombrePublico)}&background=random&size=128`}
                                  alt={prov.nombrePublico}
                                  className="provider-logo-small"
                                />
                                <h2 className="provider-name premium-name">{prov.nombrePublico}</h2>
                                {isSocio ? (
                                  <IoRibbon className="verified-icon" style={{ color: verifiedIconColor }} />
                                ) : isDiamante ? (
                                  <IoDiamond className="verified-icon" style={{ color: verifiedIconColor }} />
                                ) : (
                                  <IoStar className="verified-icon" style={{ color: verifiedIconColor }} />
                                )}
                              </div>

                              <div className="premium-line-2">
                                <StarRating rating={prov.ratingPromedio || 0} />
                                <span className="rating-count">
                                  {prov.totalCalificaciones || 0} reseñas
                                </span>
                                <span className="dot-separator">•</span>
                                <PriceRange range={prov.rangoPrecios} />
                              </div>
                            </div>

                            <div className="premium-action-col">
                              <button className="view-btn premium-btn">
                                Ver Perfil
                              </button>
                            </div>
                          </div>

                          <p className="premium-desc">
                            {prov.descripcion ? (prov.descripcion.length > 80 ? prov.descripcion.substring(0, 80) + '...' : prov.descripcion) : 'Profesional verificado y recomendado.'}
                          </p>
                        </>
                      ) : (
                        <div className="card-content-wrapper standard-unified">
                          <img
                            src={prov.logoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(prov.nombrePublico)}&background=random&size=128`}
                            alt={prov.nombrePublico}
                            className="provider-logo"
                          />
                          <div className="provider-info">
                            <div className="info-header">
                              <h2 className="provider-name">{prov.nombrePublico}</h2>
                            </div>
                            <div className="rating-row">
                              <StarRating rating={prov.ratingPromedio || 0} />
                              <span className="rating-count">({prov.totalCalificaciones || 0})</span>
                              <span className="dot-separator">•</span>
                              <PriceRange range={prov.rangoPrecios} />
                            </div>
                          </div>
                          <div className="action-column">
                            <button className="view-btn">Ver</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <KondoBottomNav activeRoute="/marketplace" />
      </main>
    </div>
  );
}

export default MarketplaceListScreen;
